import { useEffect, useState } from "react";
import { apiFetch } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const fmtR = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;

const FY27_MONTHS = ["Apr-26","May-26","Jun-26","Jul-26","Aug-26","Sep-26",
                     "Oct-26","Nov-26","Dec-26","Jan-27","Feb-27","Mar-27"];

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/dashboard").then(r => r.json()).then(d => {
      setData(d);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", fontSize: 32 }}>🧵</div>
  );

  // Chart uses monthly_revenue returned directly from dashboard API
  const chartData = FY27_MONTHS.map(m => {
    const row = (data?.monthly_revenue || []).find(r => r.month === m);
    return {
      month:  m.replace("-26","").replace("-27",""),
      plan:   row?.plan   || 0,
      actual: row?.actual || 0,
    };
  });

  const revPlan   = data?.revenue_plan         || 0;
  const revActual = data?.revenue_actual        || 0;
  const ordersRev = data?.orders_revenue_total  || 0;
  const pct       = revPlan > 0 ? Math.round((revActual / revPlan) * 100) : 0;

  const shopifyRev    = data?.orders_by_source?.["Shopify"]?.total  || 0;
  const zohoRev       = data?.orders_by_source?.["Zoho"]?.total     || 0;
  const shopifyOrders = data?.orders_by_source?.["Shopify"]?.count  || 0;
  const zohoOrders    = data?.orders_by_source?.["Zoho"]?.count     || 0;

  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ padding: "28px 28px 60px", fontFamily: "Inter, sans-serif" }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                   color: B, fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
        {greeting} 👋 Here's Embroideary at a glance
      </h1>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
                    gap: 16, marginBottom: 28 }}>
        <Card icon="📈" label="FY Revenue Plan"    value={fmtR(revPlan)}
              sub="Full year FY27 target" color="#1F4E79" />
        <Card icon="💰" label="FY27 Revenue Actual" value={fmtR(revActual)}
              sub={`${pct}% of plan · Apr 26 – Mar 27`}
              color={pct >= 100 ? "#15803D" : pct >= 70 ? "#92400E" : ROSE} />
        <Card icon="🧾" label="FY27 Paid Orders"  value={fmtR(ordersRev)}
              sub={`${data?.paid_orders || 0} paid orders this FY`} color="#2C6E49" />
        <Card icon="👥" label="Customers"          value={data?.total_customers || 0}
              sub="Shopify + Zoho" color="#6B3A2A" />
        <Card icon="🛒" label="Total Orders"       value={data?.total_orders || 0}
              sub={`${data?.pending_orders || 0} pending`} color={B} />
        <Card icon="⚠️" label="Low Stock SKUs"    value={data?.low_stock_count || 0}
              sub={data?.low_stock_skus?.slice(0,3).join(", ") || "All stocked"}
              color={data?.low_stock_count > 0 ? "#DC2626" : "#15803D"} />
      </div>

      {/* Revenue chart */}
      <div style={box}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: B, margin: 0 }}>
            FY27 Monthly Revenue — Plan vs Actual
          </h2>
          <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
            <span><span style={{ display:"inline-block", width:12, height:12,
                                  background:"#EDE0D4", borderRadius:2, marginRight:5 }}/>Plan</span>
            <span><span style={{ display:"inline-block", width:12, height:12,
                                  background:B, borderRadius:2, marginRight:5 }}/>Actual</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} barGap={2} barCategoryGap="30%">
            <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`}
                   axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(v, n) => [fmtR(v), n === "plan" ? "Plan" : "Actual"]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #EDE0D4" }}
            />
            <Bar dataKey="plan"   fill="#EDE0D4" radius={[4,4,0,0]} name="plan" />
            <Bar dataKey="actual" radius={[4,4,0,0]} name="actual">
              {chartData.map((d, i) => (
                <Cell key={i} fill={d.actual > 0 && d.actual >= d.plan ? "#2C6E49" : B} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue by source */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={box}>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>🛍️ Shopify (Online B2C) · FY27</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: B }}>{fmtR(shopifyRev)}</div>
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>{shopifyOrders} paid orders</div>
        </div>
        <div style={box}>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>🔗 Zoho Books (Offline / B2B) · FY27</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: B }}>{fmtR(zohoRev)}</div>
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>{zohoOrders} paid invoices</div>
        </div>
      </div>


    </div>
  );
}

function Card({ icon, label, value, sub, color }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "18px 20px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)", borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: B, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 11, color: "#999" }}>{sub}</div>
    </div>
  );
}

const box = {
  background: "#fff", borderRadius: 12, padding: "20px 24px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 16,
};
